import { Component, Input, OnDestroy, OnInit, inject, input, effect, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { Sort } from '@angular/material/sort';
import { Subject, takeUntil } from 'rxjs';
import { AwardsService } from '../awards.service';
import { errorToastValue, LoadingSpinnerService, ToastMessageService } from '@k12/app-services';
import { ToastMessageType, AwardRecipient, AwardSimulationResult, ProgramType, AcademicYear } from '@k12/models';
import { CfiMatTableComponent } from '@k12/tables';
import type { ColumnDefinition } from '@k12/tables';
import { FormatDatePipe } from '@k12/pipes';
import { SendOffersDialogComponent } from './send-award-dialog/send-offers-dialog.component';
import { dateRangeValidator } from '@k12/validators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-award-simulation-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CfiMatTableComponent
  ],
  templateUrl: './award-simulation-form.component.html',
  styleUrls: ['./award-simulation-form.component.scss']
})
export class AwardSimulationFormComponent implements OnInit, OnDestroy {
  public academicYear = input.required<AcademicYear | null>();
  public programType = input.required<string>();
  @Input() idPrefix: string = '';

  private fb = inject(FormBuilder);
  private awardsService = inject(AwardsService);
  private toastMessageService = inject(ToastMessageService);
  private spinnerService = inject(LoadingSpinnerService);
  private sendOfferDialog = inject(MatDialog);
  private sendOfferDialogRef: MatDialogRef<SendOffersDialogComponent>;
  private destroy$ = new Subject<void>();

  awardSimulationForm: FormGroup;

  simulationOptions: Array<{ value: string; label: string }> = [];

  secondInputLabel = 'Amount to add';
  secondInputType = 'decimal';
  secondInputPlaceholder = 'Enter amount';
  showDateRangePicker = false;

  simulationResults: AwardSimulationResult | null = null;
  displayedColumns: string[] = [
    'pid',
    'firstName',
    'lastName',
    'tier',
    'applicationDate',
    'lotteryNumber',
    'awardAmount',
    'action'
  ];

  pageSize = 25;
  currentPage = 1;
  totalCount = 0;
  sortColumn = 'applicationDate';
  sortDirection = 'asc';
  pageSizeOptions = [10, 25, 50, 100];

  columnDefinitions: ColumnDefinition[] = [];

  constructor() {
    this.awardSimulationForm = this.fb.group(
      {
        simulationType: ['', Validators.required],
        inputValue: ['', Validators.required],
        startDate: ['', Validators.required],
        endDate: ['', Validators.required]
      },
      // Award Applied is a filter over dates applications were already submitted on,
      // so the range is allowed to start in the past.
      { validators: dateRangeValidator({ allowPastDates: true }) }
    );

    effect(() => {
      const programType = this.programType();
      this.updateColumnDefinitions(programType);
      this.updateSimulationOptions(programType);
    });
  }

  ngOnInit(): void {
    const initialType = this.awardSimulationForm.get('simulationType')?.value;
    if (initialType) {
      this.updateSecondInput(initialType);
    }
    this.awardSimulationForm.get('simulationType')?.valueChanges.subscribe((value) => {
      this.updateSecondInput(value);
      this.awardSimulationForm.get('inputValue')?.reset();
      this.awardSimulationForm.get('startDate')?.reset();
      this.awardSimulationForm.get('endDate')?.reset();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateSecondInput(simulationType: string): void {
    const inputControl = this.awardSimulationForm.get('inputValue');
    const startDateControl = this.awardSimulationForm.get('startDate');
    const endDateControl = this.awardSimulationForm.get('endDate');

    // Reset pagination, clear previous results, and clear input values when simulation type changes
    this.currentPage = 1;
    this.totalCount = 0;
    this.simulationResults = null;
    inputControl?.setValue('');
    startDateControl?.setValue('');
    endDateControl?.setValue('');

    switch (simulationType) {
      case 'amount':
        this.secondInputLabel = 'Amount to add';
        this.secondInputType = 'decimal';
        this.secondInputPlaceholder = 'Enter amount';
        this.showDateRangePicker = false;
        inputControl?.setValidators([Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]);
        startDateControl?.clearValidators();
        endDateControl?.clearValidators();
        break;
      case 'tiers':
        this.secondInputLabel = 'Tier';
        this.secondInputType = 'integer';
        this.secondInputPlaceholder = 'Enter tier number';
        this.showDateRangePicker = false;
        inputControl?.setValidators([Validators.required, Validators.pattern(/^\d+$/)]);
        startDateControl?.clearValidators();
        endDateControl?.clearValidators();
        break;
      case 'numStudents':
        this.secondInputLabel = 'Number of students to award';
        this.secondInputType = 'integer';
        this.secondInputPlaceholder = 'Enter number of students';
        this.showDateRangePicker = false;
        inputControl?.setValidators([Validators.required, Validators.pattern(/^\d+$/)]);
        startDateControl?.clearValidators();
        endDateControl?.clearValidators();
        break;
      case 'dateRange':
        this.secondInputLabel = 'Award Applied Date Range';
        this.secondInputType = 'dateRange';
        this.secondInputPlaceholder = '';
        this.showDateRangePicker = true;
        inputControl?.clearValidators();
        startDateControl?.setValidators([Validators.required]);
        endDateControl?.setValidators([Validators.required]);
        break;
    }

    inputControl?.updateValueAndValidity();
    startDateControl?.updateValueAndValidity();
    endDateControl?.updateValueAndValidity();
  }

  onSimulate(showSuccessToast: boolean = true): void {
    if (this.awardSimulationForm.valid) {
      const { simulationType, inputValue, startDate, endDate } = this.awardSimulationForm.value;

      const academicYearId = this.academicYear()?.id;
      if (!academicYearId) {
        this.toastMessageService.showToast(ToastMessageType.Error, 'Academic year is not loaded. Please try again.');
        return;
      }

      this.spinnerService.showSpinner();
      const programTypeId = this.programType() === ProgramType.ESA ? 1 : 2;

      switch (simulationType) {
        case 'amount':
          this.awardsService
            .simulateAwardByAmount(
              academicYearId,
              programTypeId,
              parseFloat(inputValue),
              this.currentPage,
              this.pageSize,
              this.sortColumn,
              this.sortDirection
            )
            .subscribe({
              next: (response) => {
                this.totalCount = response.totalCount || response.data?.length || 0;
                this.pageSize = response.pageSize || this.pageSize;
                this.currentPage = response.pageNumber || this.currentPage;
                this.simulationResults = {
                  totalFunding: response.totalAmount || 0,
                  numberOfAwards: response.totalStudentCount || 0,
                  recipients: response.data || [],
                  remainingFunds: response.remainingFunds || 0
                };
                this.spinnerService.hideSpinner();
                if (showSuccessToast) {
                  this.toastMessageService.showToast(
                    ToastMessageType.Success,
                    'Award simulation by amount completed successfully'
                  );
                }
              },
              error: (error) => {
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to simulate award by amount');
                console.error('Error:', error);
              }
            });
          break;

        case 'tiers':
          this.awardsService
            .simulateAwardByTier(
              academicYearId,
              parseInt(inputValue),
              this.currentPage,
              this.pageSize,
              this.sortColumn,
              this.sortDirection
            )
            .subscribe({
              next: (response) => {
                this.totalCount = response.totalCount || response.data?.length || 0;
                this.pageSize = response.pageSize || this.pageSize;
                this.currentPage = response.pageNumber || this.currentPage;
                this.simulationResults = {
                  totalFunding: response.totalAmount || 0,
                  numberOfAwards: response.totalStudentCount || 0,
                  recipients: response.data || [],
                  remainingFunds: response.remainingFunds || 0
                };
                this.spinnerService.hideSpinner();
                if (showSuccessToast) {
                  this.toastMessageService.showToast(
                    ToastMessageType.Success,
                    'Award simulation by tier completed successfully'
                  );
                }
              },
              error: (error) => {
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to simulate award by tier');
                console.error('Error:', error);
              }
            });
          break;

        case 'numStudents':
          this.awardsService
            .simulateAwardByStudentCount(
              academicYearId,
              programTypeId,
              parseInt(inputValue),
              this.currentPage,
              this.pageSize,
              this.sortColumn,
              this.sortDirection
            )
            .subscribe({
              next: (response) => {
                this.totalCount = response.totalCount || response.data?.length || 0;
                this.pageSize = response.pageSize || this.pageSize;
                this.simulationResults = {
                  totalFunding: response.totalAmount || 0,
                  numberOfAwards: response.totalStudentCount || 0,
                  recipients: response.data || [],
                  remainingFunds: response.remainingFunds || 0
                };
                this.currentPage = response.pageNumber || this.currentPage;
                this.spinnerService.hideSpinner();
                if (showSuccessToast) {
                  this.toastMessageService.showToast(
                    ToastMessageType.Success,
                    'Award simulation by student count completed successfully'
                  );
                }
              },
              error: (error) => {
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to simulate award by student count');
                console.error('Error:', error);
              }
            });
          break;

        case 'dateRange':
          this.awardsService
            .simulateAwardByApplicationDateRange(
              academicYearId,
              programTypeId,
              FormatDatePipe.formatDateToString(startDate),
              FormatDatePipe.formatDateToString(endDate),
              this.currentPage,
              this.pageSize,
              this.sortColumn,
              this.sortDirection
            )
            .subscribe({
              next: (response) => {
                this.totalCount = response.totalCount || response.data?.length || 0;
                this.pageSize = response.pageSize || this.pageSize;
                this.currentPage = response.pageNumber || this.currentPage;
                this.simulationResults = {
                  totalFunding: response.totalAmount || 0,
                  numberOfAwards: response.totalStudentCount || 0,
                  recipients: response.data || [],
                  remainingFunds: response.remainingFunds || 0
                };
                this.spinnerService.hideSpinner();
                if (showSuccessToast) {
                  this.toastMessageService.showToast(
                    ToastMessageType.Success,
                    'Award simulation by date range completed successfully'
                  );
                }
              },
              error: (error: HttpErrorResponse) => {
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
              }
            });
          break;
      }
    }
  }

  onSend(): void {
    if (this.awardSimulationForm.valid) {
      const { simulationType, inputValue } = this.awardSimulationForm.value;

      const academicYearId = this.academicYear()?.id;
      if (!academicYearId) {
        this.toastMessageService.showToast(ToastMessageType.Error, 'Academic year is not loaded. Please try again.');
        return;
      }

      this.spinnerService.showSpinner();
      const programTypeId = this.programType() === ProgramType.ESA ? 1 : 2;

      switch (simulationType) {
        case 'amount':
          this.awardsService
            .sendAwardByAmount(academicYearId, programTypeId, this.simulationResults?.totalFunding || 0)
            .subscribe({
              next: () => {
                this.simulationResults = null;
                this.awardSimulationForm.get('inputValue')?.reset();
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(
                  ToastMessageType.Success,
                  'Award sent by amount completed successfully'
                );
              },
              error: (error) => {
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to send award by amount');
                console.error('Error:', error);
              }
            });
          break;

        case 'tiers':
          this.awardsService.sendAwardByTier(academicYearId, parseInt(inputValue)).subscribe({
            next: () => {
              this.simulationResults = null;
              this.awardSimulationForm.get('inputValue')?.reset();
              this.spinnerService.hideSpinner();
              this.toastMessageService.showToast(ToastMessageType.Success, 'Award sent by tier completed successfully');
            },
            error: (error) => {
              this.spinnerService.hideSpinner();
              this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to send award by tier');
              console.error('Error:', error);
            }
          });
          break;

        case 'numStudents':
          this.awardsService
            .sendAwardByStudentCount(academicYearId, programTypeId, this.simulationResults?.numberOfAwards || 0)
            .subscribe({
              next: () => {
                this.simulationResults = null;
                this.awardSimulationForm.get('inputValue')?.reset();
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(
                  ToastMessageType.Success,
                  'Award sent by student count completed successfully'
                );
              },
              error: (error) => {
                this.spinnerService.hideSpinner();
                this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to send award by student count');
                console.error('Error:', error);
              }
            });
          break;

        default:
          // Nothing was requested, so release the spinner instead of leaving it over the page.
          this.spinnerService.hideSpinner();
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            `Sending awards is not available for the ${this.secondInputLabel} simulation.`
          );
          break;
      }
    }
  }

  /**
   * Award Applied simulations have no matching send endpoint, so the results are
   * report-only and the send action stays closed off.
   */
  get canSendAwards(): boolean {
    return this.awardSimulationForm.get('simulationType')?.value !== 'dateRange';
  }

  getInputType(): string {
    return 'number';
  }

  getInputStep(): string {
    return this.secondInputType === 'decimal' ? '0.01' : '1';
  }

  private updateColumnDefinitions(programType: string): void {
    const baseColumns: ColumnDefinition[] = [
      { name: 'pid', header: 'PID', sortable: true, width: 12 },
      { name: 'firstName', header: 'First Name', sortable: true, width: 12 },
      { name: 'lastName', header: 'Last Name', sortable: true, width: 12 }
    ];

    const tierColumn: ColumnDefinition = { name: 'tier', header: 'Tier', sortable: true, width: 12 };
    const esaFundingLevelColumn: ColumnDefinition = {
      name: 'esaFundingLevel',
      header: 'ESA+ Funding Level',
      sortable: true,
      width: 12,
      sortKey: 'fundingLevel'
    };
    const isESA = programType === ProgramType.ESA;

    const commonColumns: ColumnDefinition[] = [
      { name: 'applicationDate', header: 'Application Date', sortable: true, width: 16 },
      { name: 'lotteryNumber', header: 'Lottery Number', sortable: true, width: 12 },
      { name: 'awardAmount', header: 'Award Amount', sortable: true, width: 12 },
      {
        name: 'action',
        header: 'Action',
        sortable: true,
        action: true,
        width: 12,
        sortKey: isESA ? 'isEsaFlagged' : 'isOsFlagged'
      }
    ];

    this.columnDefinitions = [...baseColumns, isESA ? esaFundingLevelColumn : tierColumn, ...commonColumns];

    this.displayedColumns = this.columnDefinitions.map((col) => col.name);
  }

  private updateSimulationOptions(programType: string): void {
    const allOptions = [
      { value: 'amount', label: 'Amount to Award' },
      { value: 'tiers', label: 'Tiers' },
      { value: 'numStudents', label: 'Number of students to Award' },
      { value: 'dateRange', label: 'Award Applied' }
    ];

    const isESA = programType === ProgramType.ESA;

    // Remove 'tiers' option for ESA program type
    this.simulationOptions = isESA ? allOptions.filter((option) => option.value !== 'tiers') : allOptions;

    // If current selection is 'tiers' and we switched to ESA, reset to 'amount'
    const currentValue = this.awardSimulationForm.get('simulationType')?.value;
    if (isESA && currentValue === 'tiers') {
      this.awardSimulationForm.get('simulationType')?.setValue('amount');
    }
  }

  sendAwards(): void {
    if (this.simulationResults) {
      this.sendOfferDialogRef = this.sendOfferDialog.open(SendOffersDialogComponent, {
        width: '670px',
        data: {
          programType: this.programType(),
          totalFunding: this.simulationResults.totalFunding,
          numberOfAwards: this.simulationResults.numberOfAwards,
          remainingFunds: this.simulationResults.remainingFunds
        },
        panelClass: 'custom-dialog-lg',
        backdropClass: 'custom-dialog-backdrop'
      });

      this.sendOfferDialogRef.componentInstance.onConfirmModal.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.onSend();
        this.sendOfferDialogRef.close();
      });
    }
  }

  onPageChange(event: PageEvent): void {
    if (event.pageSize && event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
    }
    this.currentPage = event.pageIndex + 1;
    this.onSimulate(false);
  }

  onSortChange(sort: unknown): void {
    const typedSort = sort as Sort;
    const columnDef = this.columnDefinitions.find((col) => col.name === typedSort.active);
    this.sortColumn = columnDef?.sortKey ?? typedSort.active;
    this.sortDirection = typedSort.direction || 'asc';
    this.currentPage = 1;
    this.onSimulate(false);
  }

  toggleFlag(recipient: AwardRecipient): void {
    const recipientKey = recipient.studentId;
    const isESA = this.programType() === ProgramType.ESA;
    const isFlagged = isESA ? recipient.isEsaFlagged : recipient.isOsFlagged;
    const academicYearId = this.academicYear()?.id;
    const programTypeId = isESA ? 1 : 2;

    if (!academicYearId) {
      this.toastMessageService.showToast(ToastMessageType.Error, 'Academic year is not loaded.');
      return;
    }

    // Call API to flag/unflag student
    this.awardsService.flagStudent(recipientKey, academicYearId, programTypeId, !isFlagged).subscribe({
      next: () => {
        // Update local state after successful API call
        if (isESA) {
          recipient.isEsaFlagged = !isFlagged;
        } else {
          recipient.isOsFlagged = !isFlagged;
        }

        const message = isFlagged ? 'Student flag removed' : 'Student flagged successfully';
        this.toastMessageService.showToast(ToastMessageType.Success, message);

        // Re-run the simulation to get updated totals
        this.onSimulate();
      },
      error: (error) => {
        this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to flag student');
        console.error('Error flagging student:', error);
      }
    });
  }

  isFlagged(studentId: string): boolean {
    const isESA = this.programType() === ProgramType.ESA;
    const recipient = this.simulationResults?.recipients.find((r) => r.studentId === studentId);
    if (!recipient) return false;

    return isESA ? recipient.isEsaFlagged : recipient.isOsFlagged;
  }
}
